import { ArrowLeftRight, Bluetooth, Clipboard, Headphones, LoaderCircle, Mic, Play, Radio, Square, Volume2 } from "lucide-react";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { API_URL, apiRequest, ApiError } from "../lib/api";
import { languageByCode, languages } from "../lib/languages";
import { isStopVoiceCommand, NativeSpeech } from "../lib/nativeSpeech";

type Mode = "face" | "speaker";
type Side = "source" | "target";
type SpeechTone = "statement" | "question" | "emergency";

interface ConversationTurn {
  id: string;
  side: Side;
  input: string;
  translation: string;
  from: string;
  to: string;
  tone: SpeechTone;
  time: string;
}

interface SpeechTicket {
  url: string;
  expiresAt: string;
}

interface NovaMessage {
  type?: string;
  is_final?: boolean;
  speech_final?: boolean;
  channel?: { alternatives?: Array<{ transcript?: string }> };
  channels?: Array<{ alternatives?: Array<{ transcript?: string }> }>;
}

function detectSpeechTone(text: string): SpeechTone {
  const normalized = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const emergencyTerms = ["emergencia", "auxilio", "ayuda", "ambulancia", "policia", "peligro", "fuego", "emergency", "help", "ambulance", "police", "danger", "fire"];
  if (emergencyTerms.some((term) => normalized.includes(term))) return "emergency";
  const questionStarts = ["que ", "como ", "cuando ", "donde ", "por que ", "quien ", "cuanto ", "what ", "how ", "when ", "where ", "why ", "who ", "can ", "could ", "do ", "does ", "is ", "are "];
  if (normalized.includes("?") || normalized.includes("¿") || questionStarts.some((term) => normalized.startsWith(term))) return "question";
  return "statement";
}

function pcm16At16Khz(input: Float32Array, inputRate: number): ArrayBuffer {
  const ratio = inputRate / 16_000;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(length);
  for (let index = 0; index < length; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.min(input.length, Math.floor((index + 1) * ratio));
    let sum = 0;
    for (let sample = start; sample < end; sample += 1) sum += input[sample] ?? 0;
    const value = Math.max(-1, Math.min(1, sum / Math.max(1, end - start)));
    output[index] = value < 0 ? value * 0x8000 : value * 0x7fff;
  }
  return output.buffer;
}

export function TranslatePage() {
  const { token, refresh } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get("mode") === "speaker" ? "speaker" : "face");
  const [source, setSource] = useState("es");
  const [target, setTarget] = useState(params.get("target") || "en");
  const [input, setInput] = useState(params.get("text") || "");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState<Side | null>(null);
  const [automatic, setAutomatic] = useState(false);
  const [automaticStatus, setAutomaticStatus] = useState("Listo para conversar");
  const [lastFrom, setLastFrom] = useState(source);
  const [lastTo, setLastTo] = useState(target);
  const [error, setError] = useState("");
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const automaticRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const streamingPausedRef = useRef(false);
  const streamingSideRef = useRef<Side>("source");
  const committedTranscriptRef = useRef("");
  const processingTurnRef = useRef(false);
  const sessionTimerRef = useRef<number | null>(null);
  const intentionalCloseRef = useRef(false);
  const nativeActiveRef = useRef(false);
  const nativeListenersRef = useRef<PluginListenerHandle[]>([]);
  const voiceAutoStartRef = useRef(false);

  const appendTurn = useCallback((heard: string, translation: string, from: string, to: string, side: Side, suppliedTone?: string) => {
    const tone: SpeechTone = suppliedTone === "question" || suppliedTone === "emergency" ? suppliedTone : detectSpeechTone(heard);
    setTurns((current) => [...current.slice(-29), {
      id: `${Date.now()}-${current.length}`,
      side,
      input: heard,
      translation,
      from,
      to,
      tone,
      time: new Intl.DateTimeFormat("es", { hour: "2-digit", minute: "2-digit" }).format(new Date()),
    }]);
  }, []);

  function unlockAudio() {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (AudioContextConstructor) {
      const context = audioContextRef.current ?? new AudioContextConstructor();
      audioContextRef.current = context;
      void context.resume();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      gain.gain.value = 0.00001;
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.03);
    }
    const warmup = new SpeechSynthesisUtterance(" ");
    warmup.volume = 0.01;
    speechSynthesis.cancel();
    speechSynthesis.speak(warmup);
    speechSynthesis.resume();
  }

  const speak = useCallback((text: string, language: string): Promise<void> => new Promise((resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = languageByCode(language)?.speechCode || language;
    utterance.rate = 0.94;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      resolve();
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);
    speechSynthesis.resume();
    window.setTimeout(() => speechSynthesis.resume(), 250);
    const maximumDuration = Math.min(15_000, Math.max(3_500, text.length * 115));
    window.setTimeout(() => {
      if (finished) return;
      speechSynthesis.cancel();
      finish();
    }, maximumDuration);
  }), []);

  const translate = useCallback(async (text: string, from = source, to = target, automaticFlow = false) => {
    const clean = text.trim();
    if (!clean || !token) return;
    setBusy(true);
    setError("");
    try {
      const result = await apiRequest<{ translation: string }>("/translate", {
        method: "POST",
        body: JSON.stringify({ text: clean, source: from, target: to }),
      }, token);
      setLastFrom(from);
      setLastTo(to);
      setOutput(result.translation);
      appendTurn(clean, result.translation, from, to, from === source ? "source" : "target");
      void refresh();
      setBusy(false);
      if (automaticFlow) setAutomaticStatus(`Reproduciendo en ${languageByCode(to)?.name ?? to}…`);
      await speak(result.translation, to);
      return result.translation;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "No se pudo traducir");
    } finally {
      setBusy(false);
    }
  }, [appendTurn, refresh, source, speak, target, token]);

  useEffect(() => {
    const initialText = params.get("text");
    if (initialText) void translate(initialText, source, target);
    // Only run for an incoming travel phrase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStreaming = useCallback(() => {
    intentionalCloseRef.current = true;
    streamingPausedRef.current = true;
    if (sessionTimerRef.current !== null) window.clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = null;
    processorRef.current?.disconnect();
    mediaSourceRef.current?.disconnect();
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    socketRef.current?.close(1000, "session-ended");
    void streamContextRef.current?.close();
    processorRef.current = null;
    mediaSourceRef.current = null;
    mediaStreamRef.current = null;
    socketRef.current = null;
    streamContextRef.current = null;
    committedTranscriptRef.current = "";
    processingTurnRef.current = false;
    if (nativeActiveRef.current) void NativeSpeech.stop().catch(() => undefined);
    nativeActiveRef.current = false;
    nativeListenersRef.current.forEach((listener) => void listener.remove());
    nativeListenersRef.current = [];
  }, []);

  useEffect(() => () => {
    automaticRef.current = false;
    recognitionRef.current?.abort();
    stopStreaming();
    speechSynthesis.cancel();
    void audioContextRef.current?.close();
  }, [stopStreaming]);

  const processStreamingTurn = useCallback(async (transcript: string) => {
    const clean = transcript.trim();
    if (!clean || processingTurnRef.current || !automaticRef.current) return;
    if (isStopVoiceCommand(clean)) {
      automaticRef.current = false;
      stopStreaming();
      setAutomatic(false);
      setListening(null);
      setInput(clean);
      setAutomaticStatus("Conversación detenida por voz");
      await speak("Traducción detenida", source);
      return;
    }
    processingTurnRef.current = true;
    streamingPausedRef.current = true;
    const side = streamingSideRef.current;
    const from = side === "source" ? source : target;
    const to = side === "source" ? target : source;
    setInput(clean);
    setAutomaticStatus(`Traduciendo al ${languageByCode(to)?.name ?? to}…`);
    await translate(clean, from, to, true);
    if (automaticRef.current) {
      const nextSide: Side = side === "source" ? "target" : "source";
      streamingSideRef.current = nextSide;
      setListening(nextSide);
      setAutomaticStatus(`Escuchando ${languageByCode(nextSide === "source" ? source : target)?.name ?? "hablar"}…`);
      streamingPausedRef.current = false;
    }
    processingTurnRef.current = false;
  }, [source, speak, stopStreaming, target, translate]);

  const startNovaStreaming = useCallback(async (): Promise<boolean> => {
    if (!token || !window.WebSocket) return false;
    try {
      if (Capacitor.isNativePlatform()) {
        const ticket = await apiRequest<SpeechTicket>("/speech/ticket", { method: "POST" }, token);
        const listeners = await Promise.all([
          NativeSpeech.addListener("partial", ({ transcript }) => {
            if (transcript) setInput(transcript);
          }),
          NativeSpeech.addListener("translating", ({ transcript, to }) => {
            if (transcript) setInput(transcript);
            setAutomaticStatus(`Traduciendo al ${languageByCode(to)?.name ?? to}…`);
          }),
          NativeSpeech.addListener("translation", ({ input: heard, translation, from, to, tone }) => {
            if (heard) setInput(heard);
            if (translation) setOutput(translation);
            if (from) setLastFrom(from);
            if (to) setLastTo(to);
            if (heard && translation && from && to) appendTurn(heard, translation, from, to, from === source ? "source" : "target", tone);
            setAutomaticStatus(`Reproduciendo en ${languageByCode(to)?.name ?? to}…`);
            void refresh();
          }),
          NativeSpeech.addListener("listening", ({ side }) => {
            const nextSide: Side = side === "target" ? "target" : "source";
            setListening(nextSide);
            setAutomaticStatus(`Escuchando ${languageByCode(nextSide === "source" ? source : target)?.name ?? "hablar"}…`);
          }),
          NativeSpeech.addListener("error", ({ message }) => {
            if (message) setError(message);
          }),
          NativeSpeech.addListener("ended", () => {
            nativeActiveRef.current = false;
            automaticRef.current = false;
            setListening(null);
            setAutomaticStatus("Sesión de prueba finalizada (15 min)");
          }),
        ]);
        nativeListenersRef.current = listeners;
        await NativeSpeech.start({ url: ticket.url, apiUrl: API_URL, token, source, target });
        nativeActiveRef.current = true;
        setListening("source");
        setAutomaticStatus(`Escuchando ${languageByCode(source)?.name ?? source} en segundo plano…`);
        return true;
      }
      if (!navigator.mediaDevices?.getUserMedia) return false;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      const ticket = await apiRequest<SpeechTicket>("/speech/ticket", { method: "POST" }, token);
      const socket = new WebSocket(ticket.url);
      socket.binaryType = "arraybuffer";
      mediaStreamRef.current = stream;
      socketRef.current = socket;
      intentionalCloseRef.current = false;
      streamingSideRef.current = "source";
      committedTranscriptRef.current = "";

      return await new Promise<boolean>((resolve) => {
        let settled = false;
        const settle = (value: boolean) => {
          if (settled) return;
          settled = true;
          resolve(value);
        };
        const connectionTimer = window.setTimeout(() => {
          settle(false);
          stopStreaming();
        }, 8_000);

        socket.onopen = () => {
          window.clearTimeout(connectionTimer);
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
          if (!AudioContextConstructor) {
            settle(false);
            stopStreaming();
            return;
          }
          const context = new AudioContextConstructor();
          const sourceNode = context.createMediaStreamSource(stream);
          // ScriptProcessor remains the most compatible PCM bridge on iOS WebKit.
          const processor = context.createScriptProcessor(2048, 1, 1);
          const silentGain = context.createGain();
          silentGain.gain.value = 0;
          processor.onaudioprocess = (event) => {
            if (socket.readyState !== WebSocket.OPEN || streamingPausedRef.current) return;
            socket.send(pcm16At16Khz(event.inputBuffer.getChannelData(0), context.sampleRate));
          };
          sourceNode.connect(processor);
          processor.connect(silentGain).connect(context.destination);
          streamContextRef.current = context;
          mediaSourceRef.current = sourceNode;
          processorRef.current = processor;
          void context.resume();
          streamingPausedRef.current = false;
          setListening("source");
          setAutomaticStatus(`Escuchando ${languageByCode(source)?.name ?? source} en vivo…`);
          sessionTimerRef.current = window.setTimeout(() => {
            stopStreaming();
            automaticRef.current = false;
            setAutomatic(false);
            setListening(null);
            setAutomaticStatus("Sesión de prueba finalizada (15 min)");
          }, 15 * 60_000);
          settle(true);
        };

        socket.onmessage = (event) => {
          if (typeof event.data !== "string" || processingTurnRef.current) return;
          let message: NovaMessage;
          try {
            message = JSON.parse(event.data) as NovaMessage;
          } catch {
            return;
          }
          const alternative = message.channel?.alternatives?.[0] ?? message.channels?.[0]?.alternatives?.[0];
          const transcript = alternative?.transcript?.trim() ?? "";
          if (transcript) {
            if (message.is_final) committedTranscriptRef.current = `${committedTranscriptRef.current} ${transcript}`.trim();
            const visible = message.is_final ? committedTranscriptRef.current : `${committedTranscriptRef.current} ${transcript}`.trim();
            setInput(visible);
          }
          if (message.speech_final || message.type === "UtteranceEnd") {
            const complete = committedTranscriptRef.current || transcript;
            committedTranscriptRef.current = "";
            if (complete) void processStreamingTurn(complete);
          }
        };

        socket.onerror = () => settle(false);
        socket.onclose = () => {
          window.clearTimeout(connectionTimer);
          if (!settled) settle(false);
          if (automaticRef.current && !intentionalCloseRef.current) {
            setError("Se cortó el audio en vivo. Podés reiniciar manos libres.");
            automaticRef.current = false;
            setAutomatic(false);
            setListening(null);
            setAutomaticStatus("Conexión interrumpida");
            stopStreaming();
          }
        };
      });
    } catch (caught) {
      stopStreaming();
      const detail = caught instanceof Error
        ? caught.message
        : typeof caught === "object" && caught !== null && "message" in caught
          ? String((caught as { message: unknown }).message)
          : "Error desconocido";
      if (Capacitor.isNativePlatform()) {
        setError(`No se pudo iniciar el audio nativo: ${detail}`);
        setAutomaticStatus("Audio nativo no disponible");
      }
      return false;
    }
  }, [appendTurn, processStreamingTurn, refresh, source, stopStreaming, target, token]);

  const listenAutomatic = useCallback((side: Side) => {
    const Constructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Constructor || !automaticRef.current) return;
    recognitionRef.current?.abort();
    const recognition = new Constructor();
    const from = side === "source" ? source : target;
    const to = side === "source" ? target : source;
    recognition.lang = languageByCode(from)?.speechCode || from;
    recognition.continuous = false;
    recognition.interimResults = true;
    let finalText = "";
    let retry = true;
    recognition.onresult = (event) => {
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index]?.[0]?.transcript ?? "";
        if (event.results[index]?.isFinal) finalText += transcript;
        else interim += transcript;
      }
      setInput(finalText || interim);
    };
    recognition.onerror = (event) => {
      if (event.error === "aborted") retry = false;
      if (event.error !== "aborted" && event.error !== "no-speech") {
        setError(event.error === "not-allowed" ? "Habilitá el micrófono para iniciar la conversación automática." : "Se interrumpió el micrófono. Intentando reconectar…");
      }
    };
    recognition.onend = () => {
      setListening(null);
      if (!automaticRef.current) return;
      if (!finalText.trim()) {
        if (retry) window.setTimeout(() => listenAutomatic(side), 350);
        return;
      }
      if (isStopVoiceCommand(finalText)) {
        automaticRef.current = false;
        setAutomatic(false);
        setListening(null);
        setAutomaticStatus("Conversación detenida por voz");
        void speak("Traducción detenida", source);
        return;
      }
      retry = false;
      setAutomaticStatus(`Traduciendo al ${languageByCode(to)?.name ?? to}…`);
      void translate(finalText, from, to, true).then(() => {
        if (!automaticRef.current) return;
        const nextSide: Side = side === "source" ? "target" : "source";
        setAutomaticStatus(`Turno: ${languageByCode(nextSide === "source" ? source : target)?.name ?? "hablar"}`);
        window.setTimeout(() => listenAutomatic(nextSide), 450);
      });
    };
    recognitionRef.current = recognition;
    setInput("");
    setListening(side);
    setAutomaticStatus(`Escuchando ${languageByCode(from)?.name ?? from}…`);
    recognition.start();
  }, [source, speak, target, translate]);

  async function startAutomatic() {
    setError("");
    unlockAudio();
    automaticRef.current = true;
    setAutomatic(true);
    setAutomaticStatus("Conectando audio de baja latencia…");
    if (await startNovaStreaming()) return;
    stopStreaming();
    // Inside the installed iOS app we must use the native AVAudioEngine path.
    // Falling back to Web Speech hides the real native error and repeatedly
    // interrupts the microphone when the WebView changes audio routes.
    if (Capacitor.isNativePlatform()) {
      automaticRef.current = false;
      setAutomatic(false);
      setListening(null);
      return;
    }
    const Constructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Constructor) {
      automaticRef.current = false;
      setAutomatic(false);
      return setError("No pudimos iniciar Nova‑3 y este navegador no ofrece reconocimiento alternativo.");
    }
    setAutomaticStatus("Usando reconocimiento compatible…");
    listenAutomatic("source");
  }

  useEffect(() => {
    if (params.get("autostart") !== "1" || voiceAutoStartRef.current || !token) return;
    voiceAutoStartRef.current = true;
    void startAutomatic();
    // The Siri command is consumed once for this mounted page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, token]);

  function stopAutomatic() {
    automaticRef.current = false;
    stopStreaming();
    setAutomatic(false);
    setAutomaticStatus("Conversación detenida");
    recognitionRef.current?.abort();
    speechSynthesis.cancel();
    setListening(null);
  }

  function startListening(side: Side) {
    const Constructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Constructor) {
      setError("El reconocimiento de voz necesita Chrome o Safari actualizado.");
      return;
    }
    recognitionRef.current?.abort();
    const recognition = new Constructor();
    const from = side === "source" ? source : target;
    const to = side === "source" ? target : source;
    recognition.lang = languageByCode(from)?.speechCode || from;
    recognition.continuous = false;
    recognition.interimResults = true;
    let finalText = "";
    recognition.onresult = (event) => {
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index]?.[0]?.transcript ?? "";
        if (event.results[index]?.isFinal) finalText += transcript;
        else interim += transcript;
      }
      setInput(finalText || interim);
    };
    recognition.onerror = (event) => {
      if (event.error !== "aborted" && event.error !== "no-speech") setError(event.error === "not-allowed" ? "Habilitá el micrófono para poder traducir tu voz." : "No pudimos escuchar. Probá nuevamente.");
      setListening(null);
    };
    recognition.onend = () => {
      setListening(null);
      if (finalText.trim()) void translate(finalText, from, to);
    };
    recognitionRef.current = recognition;
    setInput("");
    setOutput("");
    setListening(side);
    recognition.start();
  }

  function stopListening() {
    recognitionRef.current?.stop();
    setListening(null);
  }

  function swap() {
    if (automatic) stopAutomatic();
    setSource(target);
    setTarget(source);
    setLastFrom(target);
    setLastTo(source);
    setInput(output);
    setOutput(input);
  }

  function replay() {
    if (output) void speak(output, lastTo);
  }

  return (
    <div className="page-stack translate-page">
      <div className="page-title"><div><span className="section-kicker">Conversación</span><h1>Traducí en tiempo real</h1></div><div className="headphone-badge"><Headphones size={17} /> Audio XR4</div></div>
      <div className="mode-toggle"><button className={mode === "face" ? "active" : ""} onClick={() => setMode("face")}>Cara a cara</button><button className={mode === "speaker" ? "active" : ""} onClick={() => { if (automatic) stopAutomatic(); setMode("speaker"); }}>Altavoz</button></div>
      <div className="language-switcher">
        <label><span>Yo hablo</span><select value={source} onChange={(event) => { if (automatic) stopAutomatic(); setSource(event.target.value); setLastFrom(event.target.value); }}>{languages.map((language) => <option key={language.code} value={language.code}>{language.flag} {language.name}</option>)}</select></label>
        <button onClick={swap} className="swap-button" aria-label="Intercambiar idiomas"><ArrowLeftRight size={19} /></button>
        <label><span>La otra persona</span><select value={target} onChange={(event) => { if (automatic) stopAutomatic(); setTarget(event.target.value); setLastTo(event.target.value); }}>{languages.map((language) => <option key={language.code} value={language.code}>{language.flag} {language.name}</option>)}</select></label>
      </div>
      {mode === "face" && (
        <section className={automatic ? "automatic-card active" : "automatic-card"}>
          <div className="automatic-copy"><span><Radio size={20} /></span><div><strong>Conversación automática</strong><small>Nova‑3 detecta el final de cada frase y alterna los turnos sin tocar botones.</small></div></div>
          <div className="automatic-status"><i className={automatic ? "live-dot" : ""} />{automaticStatus}</div>
          <button className={automatic ? "button stop-auto" : "button primary"} onClick={() => automatic ? stopAutomatic() : void startAutomatic()}>{automatic ? <><Square size={18} /> Detener</> : <><Mic size={18} /> Iniciar manos libres</>}</button>
          <p><Bluetooth size={15} /> Seleccioná Q17 o XR4 en Bluetooth. Para terminar sin tocar el teléfono, decí “LingoSound, detener traducción”.</p>
        </section>
      )}
      {mode === "face" && (turns.length > 0 || automatic) && (
        <section className="conversation-panel" aria-label="Conversación traducida">
          <div className="conversation-heading">
            <div><strong>Conversación</strong><small>{turns.length ? `${turns.length} ${turns.length === 1 ? "turno" : "turnos"}` : "Escuchando el primer turno"}</small></div>
            {turns.length > 0 && <button onClick={() => setTurns([])}>Limpiar</button>}
          </div>
          <div className="conversation-feed" aria-live="polite">
            {turns.map((turn) => (
              <article key={turn.id} className={`conversation-bubble ${turn.side}`}>
                <div className="bubble-meta">
                  <span>{turn.side === "source" ? "Yo" : "La otra persona"} · {languageByCode(turn.from)?.flag}</span>
                  <time>{turn.time}</time>
                </div>
                <p>{turn.input}</p>
                <div className="bubble-translation">
                  <span>{languageByCode(turn.to)?.flag} {turn.translation}</span>
                  <button onClick={() => void speak(turn.translation, turn.to)} aria-label="Reproducir traducción"><Volume2 size={16} /></button>
                </div>
                {turn.tone !== "statement" && <small className={`tone-badge ${turn.tone}`}>{turn.tone === "question" ? "Pregunta" : "Emergencia"}</small>}
              </article>
            ))}
            {automatic && input.trim() && input.trim() !== turns[turns.length - 1]?.input && (
              <div className={`conversation-bubble live ${listening === "target" ? "target" : "source"}`}>
                <div className="bubble-meta"><span>Escuchando…</span><i className="live-dot" /></div>
                <p>{input}</p>
              </div>
            )}
          </div>
        </section>
      )}
      {mode === "face" && (
        <div className={automatic ? "face-buttons manual-disabled" : "face-buttons"}>
          <button disabled={automatic} className={listening === "source" ? "talk-button listening" : "talk-button source"} onClick={() => listening ? stopListening() : startListening("source")}><span>{listening === "source" ? <Square size={25} /> : <Mic size={28} />}</span><strong>{listening === "source" ? "Escuchando…" : `Hablo ${languageByCode(source)?.name}`}</strong><small>Control manual</small></button>
          <button disabled={automatic} className={listening === "target" ? "talk-button listening" : "talk-button target"} onClick={() => listening ? stopListening() : startListening("target")}><span>{listening === "target" ? <Square size={25} /> : <Mic size={28} />}</span><strong>{listening === "target" ? "Escuchando…" : `Habla ${languageByCode(target)?.name}`}</strong><small>Control manual</small></button>
        </div>
      )}
      {mode === "speaker" && <button className={listening ? "speaker-mic listening" : "speaker-mic"} onClick={() => listening ? stopListening() : startListening("source")}>{listening ? <Square size={34} /> : <Mic size={38} />}<span>{listening ? "Escuchando…" : "Tocá para hablar"}</span></button>}
      <div className="translation-workspace">
        <label><span>{languageByCode(lastFrom)?.flag} {languageByCode(lastFrom)?.name}</span><textarea value={input} onChange={(event) => setInput(event.target.value)} placeholder="Hablá o escribí acá…" maxLength={3000} /></label>
        <button className="button primary" onClick={() => void translate(input)} disabled={busy || automatic || !input.trim()}>{busy ? <><LoaderCircle size={18} className="spin" /> Traduciendo…</> : <><Play size={18} /> Traducir</>}</button>
        <div className="translation-result"><div className="result-heading"><span>{languageByCode(lastTo)?.flag} {languageByCode(lastTo)?.name}</span><div><button onClick={() => void navigator.clipboard.writeText(output)} aria-label="Copiar"><Clipboard size={18} /></button><button onClick={replay} aria-label="Reproducir"><Volume2 size={19} /></button></div></div><p className={output ? "" : "placeholder"}>{output || "La traducción aparecerá acá y se reproducirá por tus auriculares."}</p></div>
      </div>
      {error && <div className="form-error" role="alert">{error}</div>}
    </div>
  );
}
